import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, HashRouter } from "react-router-dom";
import { DotBotsAuth } from "@dotbots-boutique/auth-sdk";
import { DotBotsAuthProvider } from "@dotbots-boutique/auth-sdk/react";
import App from "./App";
import PublicApp from "./PublicApp";
import AppManual from "./pages/AppManual";
import { I18nProvider } from "./i18n/I18nProvider";
import { setLang as setI18nLang } from "./i18n/state";
import { restoreRouteBeforeMount } from "./lib/routeMemory";
import "./styles.css";

// Restore the last-known in-app route before React mounts. Doing this
// synchronously means HashRouter sees the right URL on its first render and
// no useEffect-ordering tricks are required.
restoreRouteBeforeMount();

const publicPathPrefixes = ["/ticket/nieuw", "/csat/", "/form/"];
const isPublicRoute = publicPathPrefixes.some((p) => window.location.pathname.startsWith(p));
// The manual route must be reachable via a plain URL because the
// marketplace links here from the app's Links section (DotBots guide
// 3.55.0 demands `/app-manual`, not `/#/app-manual`). Mount it under
// BrowserRouter with the auth SDK — the manual filters sections by
// the user's roles.
const isManualRoute = window.location.pathname === "/app-manual"
  || window.location.pathname.startsWith("/app-manual/");

// On public form routes we want a predictable language regardless of
// what the operator left in localStorage during the previous
// authenticated session. Default to English; when the URL pins ANY
// IETF-style code (en, nl, it, es, zh, pt-BR, …) honour it. Labels
// fall back to English in tr() where the locale isn't translated, so
// shipping a translation for a language is a future-friendly add.
const isFormRoute = window.location.pathname.startsWith("/form/");
if (isFormRoute) {
  const formLangMatch = window.location.pathname.match(
    /^\/form\/[^/]+\/([a-z]{2,3}(?:-[A-Za-z0-9]{2,8})?)\/?$/,
  );
  const pinned = formLangMatch?.[1];
  if (pinned) {
    setI18nLang(pinned);
  } else {
    setI18nLang("en");
  }
}

const root = ReactDOM.createRoot(document.getElementById("root")!);

if (isPublicRoute) {
  root.render(
    <React.StrictMode>
      <I18nProvider>
        <BrowserRouter>
          <PublicApp />
        </BrowserRouter>
      </I18nProvider>
    </React.StrictMode>,
  );
} else if (isManualRoute) {
  const auth = new DotBotsAuth({
    appId: import.meta.env.VITE_DOTBOTS_APP_ID,
    apiUrl: import.meta.env.VITE_PLATFORM_API_URL,
  });
  root.render(
    <React.StrictMode>
      <I18nProvider>
        <DotBotsAuthProvider auth={auth}>
          <AppManual />
        </DotBotsAuthProvider>
      </I18nProvider>
    </React.StrictMode>,
  );
} else {
  const auth = new DotBotsAuth({
    appId: import.meta.env.VITE_DOTBOTS_APP_ID,
    apiUrl: import.meta.env.VITE_PLATFORM_API_URL,
  });

  root.render(
    <React.StrictMode>
      <I18nProvider>
        <DotBotsAuthProvider auth={auth}>
          {/* HashRouter (instead of BrowserRouter) keeps the URL inside the
              fragment, so a browser refresh always loads index.html and the
              user lands back on the screen they were on — no Nginx SPA-
              fallback needed on the platform side. */}
          <HashRouter>
            <App />
          </HashRouter>
        </DotBotsAuthProvider>
      </I18nProvider>
    </React.StrictMode>,
  );
}
